import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './State.module.css';

// Banner — DESIGN_SYSTEM §12 row 35 (`.banner --watch --info`) and `mockups/web/w17-states.html` §3–§5.
// One anatomy everywhere a page-level message appears: an icon, a bold lead that says WHAT HAPPENED, one or
// two sentences that say whether ads and spending are affected and what to do, and optional actions on the
// right. Three tones — neutral (nothing is wrong), `watch` (amber: something is incomplete), `info` (blue:
// Meta is slowing us down, nobody is at fault). Never colour alone: every tone carries its own icon.
//
// The words are always the caller's, and a message that comes off the wire is rendered through the
// catalogue before it gets here — this component never writes a sentence.

export type BannerTone = 'neutral' | 'watch' | 'info';

export interface BannerProps {
  tone?: BannerTone;
  icon: IconName;
  /** The bold first sentence: what happened. */
  title: ReactNode;
  /** What it means for the ads and the spending, and what to do next. */
  children?: ReactNode;
  /** Usually one "Try again" button. */
  actions?: ReactNode;
  /**
   * `status` for anything informational (the default — polite), `alert` only where the user must be
   * interrupted. An account-level problem is `status`: the page is still usable and the band is permanent,
   * so an assertive interruption on every route change would be noise.
   */
  role?: 'status' | 'alert';
  testID?: string;
  className?: string;
}

export function Banner({
  tone = 'neutral',
  icon,
  title,
  children,
  actions,
  role = 'status',
  testID,
  className,
}: BannerProps) {
  return (
    <div
      role={role}
      className={cx(
        styles.banner,
        tone === 'watch' && styles.bannerWatch,
        tone === 'info' && styles.bannerInfo,
        className,
      )}
      data-testid={testID}
      data-tone={tone}
    >
      <Icon name={icon} size={16} className={styles.bannerIcon} />
      <span className={styles.bannerBody}>
        <strong className={styles.bannerTitle}>{title}</strong>
        {children ? <span className={styles.bannerText}> {children}</span> : null}
      </span>
      {actions ? <span className={styles.bannerActions}>{actions}</span> : null}
    </div>
  );
}
